import { Point, Rect, Size } from '../BasicTypes'

export interface AngleIndex {
  angle: number
  index: number
}

export interface Frame {
  /// delay in milliseconds
  readonly delay: number
  readonly region: Rect
  readonly anchor: Point
  /// The offset of name label, from base point (anchor)
  readonly nameOffset: Point
  /// The offset of dialogue bubble, from base point (anchor)
  readonly dialogueOffset: Point
}

export interface Action {
  /**
   * Frames for different angle, i.e. frames[angle index][frame index]
   */
  readonly frames: ReadonlyArray<ReadonlyArray<Frame>>
}

export default class Avatar {
  readonly imageURL: string
  readonly actions: Map<string, Action>
  /**
   *  Mapping from angle to action frames index. In increasing order.
   *  Say for example, given this angle indexes
   *
   *      [
   *           (-157.5, 0),
   *           (-112.5, 1),
   *           (-67.5, 2),
   *           (-22.5, 3),
   *           (22.5, 4),
   *           (67.5, 5),
   *           (112.5, 6),
   *           (157.5, 7),
   *           (180.0, 0),
   *      ]
   *
   *  For `0` degree angle, as `22.5` is the first one bigger than it, the result will be `4`
   */
  readonly angleIndexes: ReadonlyArray<AngleIndex>

  constructor (
    imageURL: string,
    actions: Map<string, Action>,
    angleIndexes: ReadonlyArray<AngleIndex>
  ) {
    this.imageURL = imageURL
    this.actions = actions
    this.angleIndexes = angleIndexes
  }

  static makeGridAction ({
    angleCount,
    frameCount,
    frameSize,
    anchor,
    delay,
    xOffset,
    yOffset
  }: {
    angleCount: number
    frameCount: number
    frameSize: Size
    anchor: Point
    delay: number
    xOffset: number
    yOffset: number
  }): Action {
    let frames: Frame[][] = []
    for (let angleIndex = 0; angleIndex < angleCount; ++angleIndex) {
      let angleFrames: Frame[] = []
      for (let frameIndex = 0; frameIndex < frameCount; ++frameIndex) {
        angleFrames.push({
          delay,
          anchor,
          region: {
            origin: {
              x: (frameIndex + xOffset) * frameSize.width,
              y: (angleIndex + yOffset) * frameSize.height
            },
            size: frameSize
          },
          nameOffset: {
            x: 0,
            y: 90
          },
          dialogueOffset: {
            x: 0,
            y: 110
          }
        })
      }
      frames.push(angleFrames)
    }
    return { frames }
  }

  /**
   * Create zombie avatar
   */
  static makeZombie (imagePath: string = '/images/zombie_0.png'): Avatar {
    const actions = new Map<string, Action>([
      [
        'stand',
        Avatar.makeGridAction({
          angleCount: 8,
          frameCount: 4,
          frameSize: {
            width: 128,
            height: 128
          },
          anchor: {
            x: 64,
            y: 100
          },
          delay: 250,
          xOffset: 0,
          yOffset: 0
        })
      ],
      [
        'walk',
        Avatar.makeGridAction({
          angleCount: 8,
          frameCount: 8,
          frameSize: {
            width: 128,
            height: 128
          },
          anchor: {
            x: 64,
            y: 100
          },
          delay: 125,
          xOffset: 4,
          yOffset: 0
        })
      ]
    ] as [string, Action][])
    const angleIndexes = [
      { angle: -157.5, index: 0 },
      { angle: -112.5, index: 1 },
      { angle: -67.5, index: 2 },
      { angle: -22.5, index: 3 },
      { angle: 22.5, index: 4 },
      { angle: 67.5, index: 5 },
      { angle: 112.5, index: 6 },
      { angle: 157.5, index: 7 },
      { angle: 180.0, index: 0 }
    ] as AngleIndex[]
    return new Avatar(imagePath, actions, angleIndexes)
  }

  /**
   * Get frame angle index for given angle
   * @param angle the angle of index to getting from
   */
  indexForAngle (angle: number): number {
    let index = 0
    for (const angleIndex of this.angleIndexes) {
      const currentAngle = angleIndex.angle
      if (angle <= currentAngle) {
        index = angleIndex.index
        break
      }
    }
    return index
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/models/MapObjects/Avatar.ts